'use client';

import React, { useState } from 'react';
import { Search, Trash2, CheckCircle2, XCircle, AlertCircle, Clock, ChevronDown } from 'lucide-react';
import { Appointment } from './types';

interface BookingsListViewProps {
  appointments: Appointment[];
  selectedDate: string;
  onSelectAppointment: (appointment: Appointment) => void;
  onUpdateStatus: (appointmentId: string, newStatus: string) => void;
}

export const BookingsListView: React.FC<BookingsListViewProps> = ({
  appointments,
  selectedDate,
  onSelectAppointment,
  onUpdateStatus,
}) => {
  const [rangeFilter, setRangeFilter] = useState<'dia' | 'semana' | 'mes' | 'todo'>('dia');
  const [groupFilter, setGroupFilter] = useState<'turno' | 'cliente' | 'eliminados'>('turno');
  const [clientSearch, setClientSearch] = useState('');

  // Filtrado
  const displayedAppointments = appointments.filter((a) => {
    if (groupFilter === 'eliminados') {
      return a.status.toLowerCase() === 'cancelled';
    }
    const matchesClient =
      clientSearch.trim() === '' ||
      a.clientName.toLowerCase().includes(clientSearch.toLowerCase()) ||
      a.clientPhone.includes(clientSearch);

    return matchesClient;
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs mx-4 sm:mx-6 overflow-hidden mb-12">
      {/* Barra de Controles Superiores (AppGenda Fase 3) */}
      <div className="p-4 border-b border-slate-200/90 flex flex-wrap items-center justify-between gap-4 bg-slate-50/50">
        {/* Filtro de Rango Temporal [Día | Semana | Mes | Todo] */}
        <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200/80">
          {(['dia', 'semana', 'mes', 'todo'] as const).map((r) => (
            <button
              key={r}
              onClick={() => setRangeFilter(r)}
              className={`px-3 py-1 rounded-lg text-xs font-bold capitalize transition cursor-pointer ${
                rangeFilter === r
                  ? 'bg-[#047857] text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {r === 'dia' ? 'Día' : r === 'semana' ? 'Semana' : r === 'mes' ? 'Mes' : 'Todo'}
            </button>
          ))}
        </div>

        {/* Selector [Por turno | Por cliente | Ver eliminados] */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setGroupFilter('turno')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
              groupFilter === 'turno'
                ? 'bg-[#047857] text-white shadow-xs'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Por turno
          </button>

          <button
            onClick={() => setGroupFilter('cliente')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
              groupFilter === 'cliente'
                ? 'bg-[#047857] text-white shadow-xs'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Por cliente
          </button>

          <button
            onClick={() => setGroupFilter('eliminados')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer ${
              groupFilter === 'eliminados'
                ? 'bg-rose-600 text-white shadow-xs'
                : 'bg-white border border-slate-200 text-slate-500 hover:text-rose-600'
            }`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Ver eliminados</span>
          </button>
        </div>
      </div>

      {/* Buscador de Cliente integrado */}
      <div className="px-4 py-2.5 bg-white border-b border-slate-100 flex items-center gap-2 max-w-sm">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={clientSearch}
          onChange={(e) => setClientSearch(e.target.value)}
          placeholder="Buscar por cliente o teléfono..."
          className="w-full text-xs font-medium text-[#0f172a] placeholder-slate-400 focus:outline-none bg-transparent"
        />
      </div>

      {/* Tabla Contable de 14 Columnas (Referencia Fase 3) */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs whitespace-nowrap">
          <thead className="bg-slate-50 border-b border-slate-200/80 text-[10px] font-black uppercase text-slate-500 tracking-wider">
            <tr>
              <th className="py-3 px-3">HORA</th>
              <th className="py-3 px-3">CLIENTE</th>
              <th className="py-3 px-3">SERVICIO</th>
              <th className="py-3 px-3">PROFESIONAL</th>
              <th className="py-3 px-3">ESTADO</th>
              <th className="py-3 px-3 text-right">LISTA</th>
              <th className="py-3 px-3 text-right">EFECTIVO</th>
              <th className="py-3 px-3 text-right">DESCUENTO</th>
              <th className="py-3 px-3">MOTIVO</th>
              <th className="py-3 px-3 text-right">SEÑA</th>
              <th className="py-3 px-3 text-right">TARJETA</th>
              <th className="py-3 px-3 text-right">TRANSF.</th>
              <th className="py-3 px-3 text-right">TOTAL</th>
              <th className="py-3 px-3 text-center">ACCIONES</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
            {displayedAppointments.length === 0 ? (
              <tr>
                <td colSpan={14} className="py-12 text-center text-slate-400 text-xs">
                  No se encontraron turnos para este criterio de búsqueda.
                </td>
              </tr>
            ) : (
              displayedAppointments.map((apt) => {
                const listPrice = Math.round(apt.price * 1.15);
                const isOverturn = apt.isOverturn;
                const statusKey = apt.status.toLowerCase();

                return (
                  <tr
                    key={apt.id}
                    onClick={() => onSelectAppointment(apt)}
                    className="hover:bg-slate-50/80 cursor-pointer transition"
                  >
                    {/* Hora */}
                    <td className="py-3 px-3 font-bold text-[#0f172a]">
                      {apt.startTime}
                    </td>

                    {/* Cliente */}
                    <td className="py-3 px-3">
                      <span className="font-extrabold text-[#0f172a] block">
                        {apt.clientName}
                      </span>
                      <span className="text-[10px] text-slate-400 block font-normal">
                        {apt.clientPhone}
                      </span>
                    </td>

                    {/* Servicio */}
                    <td className="py-3 px-3">
                      <span className="font-semibold">{apt.serviceName}</span>
                      <span className="text-[10px] text-slate-400 block">
                        {apt.duration} min
                      </span>
                    </td>

                    {/* Profesional */}
                    <td className="py-3 px-3">
                      <span className="inline-flex items-center gap-1.5 font-bold">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: apt.staffColor || '#10b981' }}
                        />
                        {apt.staffName}
                      </span>
                    </td>

                    {/* Estado */}
                    <td className="py-3 px-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                          statusKey === 'completed'
                            ? 'bg-sky-100 text-sky-800'
                            : statusKey === 'cancelled'
                            ? 'bg-rose-100 text-rose-800'
                            : isOverturn
                            ? 'bg-amber-100 text-amber-900 border border-amber-300'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {isOverturn ? 'Sobreturno' : apt.status}
                      </span>
                    </td>

                    {/* Precio Lista */}
                    <td className="py-3 px-3 text-right font-medium text-slate-500">
                      ${' '}
                      {listPrice.toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                    </td>

                    {/* Precio Efectivo */}
                    <td className="py-3 px-3 text-right font-bold text-[#0f172a]">
                      ${' '}
                      {apt.price.toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                    </td>

                    {/* Descuento */}
                    <td className="py-3 px-3 text-right text-slate-400">$ 0</td>

                    {/* Motivo */}
                    <td className="py-3 px-3 text-slate-400 text-[11px]">—</td>

                    {/* Seña */}
                    <td className="py-3 px-3 text-right font-semibold text-emerald-700">
                      ${' '}
                      {(apt.depositAmount || 0).toLocaleString('es-AR', {
                        minimumFractionDigits: 0,
                      })}
                    </td>

                    {/* Tarjeta */}
                    <td className="py-3 px-3 text-right text-slate-400">—</td>

                    {/* Transferencia */}
                    <td className="py-3 px-3 text-right text-slate-400">—</td>

                    {/* Total */}
                    <td className="py-3 px-3 text-right font-black text-[#0f172a]">
                      ${' '}
                      {apt.price.toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                    </td>

                    {/* Acciones */}
                    <td
                      className="py-3 px-3 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <select
                        value={apt.status.toLowerCase()}
                        onChange={(e) => onUpdateStatus(apt.id, e.target.value)}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-bold rounded-lg px-2 py-1 cursor-pointer focus:outline-none"
                      >
                        <option value="confirmed">Confirmado</option>
                        <option value="completed">Completado</option>
                        <option value="no_show">No vino</option>
                        <option value="cancelled">Cancelar</option>
                      </select>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pie de tabla con conteo */}
      <div className="p-3 border-t border-slate-200/80 bg-slate-50/50 text-[11px] font-bold text-slate-500">
        {displayedAppointments.length}{' '}
        {displayedAppointments.length === 1 ? 'turno' : 'turnos'}
      </div>
    </div>
  );
};
